import React, { useState, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import BilingualText from '../BilingualText/BilingualText';
import CompanyUsersService from '../../../services/CompanyUsersService';

const SuperAdminByCompanyBranchSearchSelect = ({
    value = '',
    onChange,
    companyId = null, // Required: Company ID to filter users
    branchId = null,  // Required: Branch ID to filter Admin users
    placeholder = "Search and select SuperAdmin/Admin...",
    name = "userId",
    label = "User",
    required = false,
    disabled = false,
    error = '',
    className = '',
    showLabel = true,
    allowClear = true,
    multiple = false,
    size = 'md' // sm, md, lg
}) => {
    const { t } = useTranslation();
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [dropdownPosition, setDropdownPosition] = useState('bottom');
    
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Load users when companyId or branchId changes
    useEffect(() => {
        if (companyId && branchId) {
            loadUsers();
        } else {
            // Clear options when company or branch is not selected
            setOptions([]);
            setFilteredOptions([]);
            setSelectedOption(null);
            setSelectedOptions([]);
            setSearchTerm('');
        }
    }, [companyId, branchId]);

    // Handle value prop changes
    useEffect(() => {
        if (multiple) {
            const values = Array.isArray(value) ? value : (value ? [value] : []);
            const selected = options.filter(opt => values.includes(opt.userId));
            setSelectedOptions(selected);
        } else {
            const selected = options.find(opt => opt.userId === value) || null;
            setSelectedOption(selected);
            setSearchTerm(selected ? `${selected.name} (${selected.userCode})` : '');
        }
    }, [value, options, multiple]);

    // Filter options based on search
    useEffect(() => {
        if (!searchTerm) {
            setFilteredOptions(options);
        } else {
            const filtered = options.filter(option =>
                option.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (option.userCode && option.userCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (option.email && option.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (option.phoneNumber && option.phoneNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (option.roleName && option.roleName.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (option.branchName && option.branchName.toLowerCase().includes(searchTerm.toLowerCase()))
            );
            setFilteredOptions(filtered);
        }
    }, [searchTerm, options]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                // Reset search term if no selection
                if (!multiple && !selectedOption) {
                    setSearchTerm('');
                } else if (!multiple && selectedOption) {
                    setSearchTerm(`${selectedOption.name} (${selectedOption.userCode})`);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [selectedOption, multiple]);

    const loadUsers = async () => {
        if (!companyId || !branchId) return;
        
        try {
            setLoading(true);
            const response = await CompanyUsersService.getSuperAdminAndAdmins(companyId, branchId);
            if (response.success && response.data) {
                setOptions(response.data);
            } else {
                setOptions([]);
            }
        } catch (error) {
            console.error('Error loading SuperAdmin and Admin users:', error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setIsOpen(true);
        
        // Clear selection if search is cleared
        if (!term && !multiple) {
            setSelectedOption(null);
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: null, userData: null }
                };
                onChange(syntheticEvent);
            }
        }
    };

    const handleOptionSelect = (option) => {
        if (multiple) {
            const isSelected = selectedOptions.some(opt => opt.userId === option.userId);
            let newSelection;
            
            if (isSelected) {
                newSelection = selectedOptions.filter(opt => opt.userId !== option.userId);
            } else {
                newSelection = [...selectedOptions, option];
            }
            
            setSelectedOptions(newSelection);
            const values = newSelection.map(opt => opt.userId);
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: values }
                };
                onChange(syntheticEvent);
            }
            setSearchTerm('');
        } else {
            setSelectedOption(option);
            setSearchTerm(`${option.name} (${option.userCode})`);
            setIsOpen(false);
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: option.userId, userData: option }
                };
                onChange(syntheticEvent);
            }
        }
    };

    const handleRemoveOption = (optionToRemove) => {
        if (multiple) {
            const newSelection = selectedOptions.filter(opt => opt.userId !== optionToRemove.userId);
            setSelectedOptions(newSelection);
            const values = newSelection.map(opt => opt.userId);
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: values }
                };
                onChange(syntheticEvent);
            }
        }
    };

    const handleClear = () => {
        if (multiple) {
            setSelectedOptions([]);
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: [], userData: null }
                };
                onChange(syntheticEvent);
            }
        } else {
            setSelectedOption(null);
            setSearchTerm('');
            if (onChange) {
                const syntheticEvent = {
                    target: { name, value: null, userData: null }
                };
                onChange(syntheticEvent);
            }
        }
        setIsOpen(false);
    };

    const handleInputFocus = () => {
        if (!companyId || !branchId) {
            return; // Don't open dropdown if company or branch is not selected
        }
        
        setIsOpen(true);
        if (!multiple && selectedOption) {
            setSearchTerm('');
        }
        
        // Calculate dropdown position
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;
            
            // If there's less than 250px below and more space above, show dropdown above
            if (spaceBelow < 250 && spaceAbove > spaceBelow) {
                setDropdownPosition('top');
            } else {
                setDropdownPosition('bottom');
            }
        }
    };

    const sizeClasses = {
        sm: 'form-control-sm',
        md: '',
        lg: 'form-control-lg'
    };

    const getRoleBadgeClass = (roleCode) => {
        switch (roleCode) {
            case 'SUPERADMIN':
                return 'badge bg-danger';
            case 'ADMIN':
                return 'badge bg-primary';
            default:
                return 'badge bg-secondary';
        }
    };

    // Determine if component should be disabled
    const isDisabled = disabled || !companyId || !branchId;

    return (
        <div className={`superadmin-by-company-branch-search-select ${className}`} ref={dropdownRef} style={{ position: 'relative' }}>
            {showLabel && (
                <Form.Label className="form-label">
                    {label} {required && <span className="text-danger">*</span>}
                </Form.Label>
            )}
            
            <div className="position-relative" style={{ zIndex: isOpen ? 1040 : 'auto' }}>
                {/* Selected options for multiple select */}
                {multiple && selectedOptions.length > 0 && (
                    <div className="selected-options mb-2">
                        {selectedOptions.map((option) => (
                            <span key={option.userId} className="badge bg-primary me-1 mb-1">
                                {option.name} ({option.userCode})
                                <button
                                    type="button"
                                    className="btn-close btn-close-white ms-1"
                                    style={{ fontSize: '0.75em' }}
                                    onClick={() => handleRemoveOption(option)}
                                    disabled={isDisabled}
                                />
                            </span>
                        ))}
                    </div>
                )}
                
                {/* Search Input */}
                <div className="input-group">
                    <Form.Control
                        ref={inputRef}
                        type="text"
                        value={searchTerm}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        placeholder={!companyId || !branchId ? "Select company and branch first..." : placeholder}
                        disabled={isDisabled || loading}
                        className={`${sizeClasses[size]} ${error ? 'is-invalid' : ''}`}
                        autoComplete="off"
                    />
                    
                    {/* Clear Button */}
                    {allowClear && (multiple ? selectedOptions.length > 0 : selectedOption) && !isDisabled && (
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleClear}
                            style={{ borderLeft: 'none' }}
                        >
                            <i className="fa fa-times"></i>
                        </button>
                    )}
                    
                    {/* Loading Spinner */}
                    {loading && (
                        <div className="input-group-text">
                            <div className="spinner-border spinner-border-sm" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Dropdown Options */}
                {isOpen && !isDisabled && companyId && branchId && (
                    <div 
                        className="dropdown-menu show w-100 shadow-lg" 
                        style={{ 
                            maxHeight: '250px', 
                            minHeight: '50px',
                            overflowY: 'auto', 
                            overflowX: 'hidden',
                            zIndex: 1050,
                            position: 'absolute',
                            ...(dropdownPosition === 'top' ? {
                                bottom: '100%',
                                top: 'auto',
                                marginBottom: '2px'
                            } : {
                                top: '100%',
                                bottom: 'auto',
                                marginTop: '2px'
                            }),
                            left: 0,
                            right: 0,
                            border: '1px solid #dee2e6',
                            borderRadius: '0.375rem',
                            backgroundColor: '#fff',
                            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
                            display: 'block',
                            padding: '0'
                        }}
                    >
                        {loading ? (
                            <div className="dropdown-item text-center py-3" style={{ 
                                margin: '0',
                                border: 'none',
                                backgroundColor: 'transparent'
                            }}>
                                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                Loading users...
                            </div>
                        ) : filteredOptions.length > 0 ? (
                            <div style={{ 
                                maxHeight: '200px', 
                                overflowY: 'auto',
                                overflowX: 'hidden'
                            }}>
                                {filteredOptions.map((option) => {
                                    const isSelected = multiple 
                                        ? selectedOptions.some(opt => opt.userId === option.userId)
                                        : selectedOption?.userId === option.userId;
                                    
                                    return (
                                        <button
                                            key={option.userId}
                                            type="button"
                                            className={`dropdown-item d-flex justify-content-between align-items-start py-2 px-3 ${isSelected ? 'active' : ''}`}
                                            onClick={() => handleOptionSelect(option)}
                                            style={{ 
                                                border: 'none',
                                                textAlign: 'left',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                width: '100%',
                                                backgroundColor: isSelected ? '#0d6efd' : 'transparent',
                                                color: isSelected ? '#fff' : '#212529'
                                            }}
                                        >
                                            <div className="flex-grow-1" style={{ 
                                                minWidth: 0,
                                                overflow: 'hidden'
                                            }}>
                                                <div className="d-flex align-items-center mb-1">
                                                    <span className="fw-semibold me-2" style={{ 
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>
                                                        {option.name}
                                                    </span>
                                                    <span className={getRoleBadgeClass(option.roleCode)} style={{ 
                                                        fontSize: '0.7rem',
                                                        padding: '0.15rem 0.4rem',
                                                        flexShrink: 0
                                                    }}>
                                                        {option.roleCode}
                                                    </span>
                                                    <span className={`badge ms-1 ${option.status === 'Active' ? 'bg-success' : 'bg-secondary'}`} style={{ 
                                                        fontSize: '0.65rem',
                                                        padding: '0.15rem 0.4rem',
                                                        flexShrink: 0
                                                    }}>
                                                        {option.status}
                                                    </span>
                                                </div>
                                                <small className={`d-block ${isSelected ? 'text-light' : 'text-muted'}`} style={{ 
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {option.userCode} • {option.phoneNumber || option.email || 'N/A'}
                                                </small>
                                                <small className={`d-block ${isSelected ? 'text-light' : 'text-info'}`} style={{ 
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {option.companyName} • {option.branchName || 'All Branches'}
                                                </small>
                                            </div>
                                            {isSelected && <i className="fa fa-check ms-2" style={{ flexShrink: 0 }}></i>}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="dropdown-item text-muted text-center py-3" style={{ 
                                margin: '0',
                                border: 'none',
                                backgroundColor: 'transparent'
                            }}>
                                {searchTerm ? 'No users found' : 'No SuperAdmin/Admin users available'}
                            </div>
                        )}
                        
                        {/* Refresh Option */}
                        <div className="dropdown-divider" style={{ 
                            margin: '0.5rem 0',
                            borderTop: '1px solid #dee2e6'
                        }}></div>
                        <button
                            type="button"
                            className="dropdown-item text-primary py-2 px-3"
                            onClick={loadUsers}
                            disabled={loading}
                            style={{ 
                                border: 'none',
                                textAlign: 'left',
                                backgroundColor: 'transparent',
                                width: '100%'
                            }}
                        >
                            <i className="fa fa-refresh me-2"></i>
                            Refresh Users
                        </button>
                    </div>
                )}
            </div>
            
            {/* Error Message */}
            {error && <div className="invalid-feedback d-block">{error}</div>}
            
            {/* Help Text */}
            {!error && (
                <Form.Text className="text-muted">
                    {!companyId || !branchId ? (
                        <BilingualText translationKey="common.selectCompanyAndBranchFirst" />
                    ) : multiple ? (
                        <BilingualText translationKey="common.searchAndSelectMultipleUsers" />
                    ) : (
                        <BilingualText translationKey="common.searchAndSelectUser" />
                    )}
                </Form.Text>
            )}
        </div>
    );
};

export default SuperAdminByCompanyBranchSearchSelect;
